import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui/Button'

import { QuestQuizFeedback } from './QuestQuizFeedback'
import { useQuizStepMutation } from '../model/mutations'
import { useQuizPlayQuery } from '../model/queries'
import { resolveQuizUiVariant } from '../model/quizKindMapping'
import { buildQuizStepSelection } from '../model/quizSelection'
import { tokenizeSortedWordsPool } from '../model/sortedWords'
import { QuizChoices } from './quiz/QuizChoices'
import { QuizMosaic } from './quiz/QuizMosaic'
import { QuizSortedWords } from './quiz/QuizSortedWords'
import { QuizTextAnswer } from './quiz/QuizTextAnswer'
import { QuizThisOrThat } from './quiz/QuizThisOrThat'

import type { QuestRun } from '../model/types'

type QuestQuizStepProps = {
  eventSlug: string
  questUid: string
  runId: number
  onFinished: (run: QuestRun) => void
}

export function QuestQuizStep({ eventSlug, questUid, runId, onFinished }: QuestQuizStepProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useQuizPlayQuery(eventSlug, questUid, runId)
  const stepMutation = useQuizStepMutation(eventSlug, questUid, runId)

  const [stepIndex, setStepIndex] = useState<number | null>(null)
  const [value, setValue] = useState<string | string[]>('')
  const [feedback, setFeedback] = useState<{ correct: boolean; pointsDelta: number } | null>(
    null
  )
  const [pendingFinish, setPendingFinish] = useState<QuestRun | null>(null)

  const questions = data?.records ?? []

  useEffect(() => {
    if (stepIndex === null && data) {
      setStepIndex(data.quiz_resume?.answered_count ?? 0)
    }
  }, [stepIndex, data])

  const currentQuestion = stepIndex !== null ? questions[stepIndex] : undefined
  const variant = useMemo(
    () => (currentQuestion ? resolveQuizUiVariant(currentQuestion) : 'choice'),
    [currentQuestion]
  )
  const sortedPool = useMemo(
    () => (currentQuestion ? tokenizeSortedWordsPool(currentQuestion.meta) : []),
    [currentQuestion]
  )

  if (isLoading || !currentQuestion) return null

  const submit = async () => {
    const selection = buildQuizStepSelection(variant, currentQuestion.uid, value)
    const result = await stepMutation.mutateAsync(selection)

    setFeedback({ correct: result.quiz_step.correct, pointsDelta: result.quiz_step.points_delta })
    if (result.quiz_step.finished) {
      setPendingFinish(result.record)
    }
  }

  const handleContinue = () => {
    setFeedback(null)

    if (pendingFinish) {
      onFinished(pendingFinish)
      return
    }

    setStepIndex(index => (index ?? 0) + 1)
    setValue('')
  }

  const canSubmit =
    variant === 'mosaic' || variant === 'sorted'
      ? (value as string[]).length > 0
      : !!(value as string)

  return (
    <div className="flex w-full max-w-sm flex-col gap-4">
      <p className="m-0 text-sm font-bold text-gray-900">{currentQuestion.name}</p>

      {variant === 'choice' && (
        <QuizChoices
          alternatives={currentQuestion.quiz_question_alternatives ?? []}
          value={(value as string) || null}
          disabled={!!feedback}
          onChange={setValue}
        />
      )}
      {variant === 'this_or_that' && (
        <QuizThisOrThat
          alternatives={currentQuestion.quiz_question_alternatives ?? []}
          value={(value as string) || null}
          disabled={!!feedback}
          onChange={setValue}
        />
      )}
      {variant === 'mosaic' && (
        <QuizMosaic
          alternatives={currentQuestion.quiz_question_alternatives ?? []}
          value={value as string[]}
          disabled={!!feedback}
          onChange={setValue}
        />
      )}
      {variant === 'text' && (
        <QuizTextAnswer value={value as string} disabled={!!feedback} onChange={setValue} />
      )}
      {variant === 'sorted' && (
        <QuizSortedWords
          pool={sortedPool}
          value={value as string[]}
          disabled={!!feedback}
          onChange={setValue}
        />
      )}

      {feedback ? (
        <QuestQuizFeedback
          correct={feedback.correct}
          pointsDelta={feedback.pointsDelta}
          onContinue={handleContinue}
        />
      ) : (
        <Button
          variant="primary"
          size="lg"
          disabled={!canSubmit}
          loading={stepMutation.isPending}
          onClick={submit}
        >
          {t('quests.quiz.submit')}
        </Button>
      )}
    </div>
  )
}
